const TYPEAHEAD_RESET_MS = 500;

class AriaListbox extends HTMLElement {
  static observedAttributes = ["active"];

  typeahead = "";

  handleKeyDown = (e) => {
    if (!this.active) return;

    switch (e.code) {
      case "ArrowUp":
        e.preventDefault();
        this._previous();
        break;
      case "ArrowDown":
        e.preventDefault();
        this._next();
        break;
      case "Home":
        e.preventDefault();
        this._first();
        break;
      case "End":
        e.preventDefault();
        this._last();
        break;
      case "Enter":
      case "Space":
        if (!this.focused[0]) break;
        e.preventDefault();
        this._select(this.focused[0]);
        break;
      default:
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
          this._typeahead(e.key);
        }
        break;
    }
  };

  handleClick = (e) => {
    const option = e.target.closest(
      '[role="option"]:not([aria-disabled="true"])',
    );
    if (!option) return;

    this._select(option);
  };

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "active" && oldValue === null && newValue !== null) {
      this._focus(this.selected[0]);
    }
  }

  connectedCallback() {
    document.addEventListener("keydown", this.handleKeyDown);
    this.addEventListener("click", this.handleClick);
    this.options.forEach((el) => {
      el.tabIndex = el.getAttribute("aria-selected") === "true" ? 0 : -1;
    });
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.handleKeyDown);
    this.removeEventListener("click", this.handleClick);
  }

  get active() {
    return this.hasAttribute("active");
  }

  get options() {
    return [
      ...this.querySelectorAll('[role="option"]:not([aria-disabled="true"])'),
    ];
  }

  get selected() {
    return [...this.querySelectorAll('[role="option"][aria-selected="true"]')];
  }

  get focused() {
    return [...this.querySelectorAll('[role="option"][data-focused]')];
  }

  _select(option) {
    this.options.forEach((el) => {
      el.tabIndex = -1;
    });
    this.selected.forEach((el) => el.setAttribute("aria-selected", "false"));
    option.setAttribute("aria-selected", "true");
    option.tabIndex = 0;
    this._setInputValue(option.dataset.value);
    option.dispatchEvent(
      new CustomEvent("aria-listbox:change", {
        bubbles: true,
        detail: { value: option.dataset.value },
      }),
    );
  }

  // Jumps to the next option starting with the letters typed in quick
  // succession, as a native select does.
  _typeahead(key) {
    clearTimeout(this.typeaheadTimer);
    this.typeaheadTimer = setTimeout(() => {
      this.typeahead = "";
    }, TYPEAHEAD_RESET_MS);
    this.typeahead += key.toLowerCase();

    // One letter, or the same letter repeated, steps through the options
    // starting with it; anything longer refines the current match.
    const repeated = [...this.typeahead].every(
      (char) => char === this.typeahead[0],
    );
    const search = repeated ? this.typeahead[0] : this.typeahead;
    const options = this.options;
    const start =
      options.findIndex((el) => el.hasAttribute("data-focused")) +
      (repeated ? 1 : 0);
    const match = [...options.slice(start), ...options.slice(0, start)].find(
      (el) => el.textContent.trim().toLowerCase().startsWith(search),
    );

    if (match) this._focus(match);
  }

  _setInputValue(value) {
    const input = this.querySelector("[data-input]");

    if (!input) return;

    input.value = value;
  }

  _first() {
    this._focus(this.options[0]);
  }

  _last() {
    const options = this.options;
    this._focus(options[options.length - 1]);
  }

  _next() {
    const options = this.options;
    const currentIndex = options.findIndex((el) =>
      el.hasAttribute("data-focused"),
    );
    const newIndex = Math.min(currentIndex + 1, options.length - 1);
    if (newIndex === currentIndex) return;

    this._focus(options[newIndex]);
  }

  _previous() {
    const options = this.options;
    const currentIndex = options.findIndex((el) =>
      el.hasAttribute("data-focused"),
    );
    const newIndex = Math.max(currentIndex - 1, 0);
    if (newIndex === currentIndex) return;

    this._focus(options[newIndex]);
  }

  _focus(el) {
    this.options.forEach((el) => {
      el.tabIndex = -1;
      el.removeAttribute("data-focused");
    });
    if (!el) return;
    el.tabIndex = 0;
    el.setAttribute("data-focused", "");
    el.focus();
  }
}

if (!customElements.get("aria-listbox")) {
  customElements.define("aria-listbox", AriaListbox);
}
